import { Draft } from 'immer';
import { assertProjectionIdentities } from './assertProjectionIdentities';
import { PolicyProjectionStrategy } from './strategyTypes';

export interface ProcessClaimProjectionOptions {
  // If true, every month is checked against assertProjectionIdentities and a
  // failure throws. Defaults to true — strategies are bugs if identities
  // fail, and we want them to surface loudly during shadow mode.
  assertIdentities?: boolean;
}

export interface ProcessClaimProjectionResult<TState = unknown, TDeltas = unknown> {
  projection: ClaimProjection<TState, TDeltas>;
  finalDeathBenefit: number;
  closingState: MonthlyState<TState>;
}

export function processClaimProjection<TState, TDeltas>(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  strategy: PolicyProjectionStrategy<TState, TDeltas>,
  options: ProcessClaimProjectionOptions = {},
): ProcessClaimProjectionResult<TState, TDeltas> {
  const { assertIdentities = true } = options;
  const { phaseCarePlans, monthlyCareInputs } = mutableClient.projectionInputs;

  const phaseLookup = new Map(phaseCarePlans.map(plan => [plan.carePhase, plan]));

  const initialState = strategy.initClaimState(mutableClient, ltcPolicy);
  const terms = strategy.buildClaimTerms(mutableClient, ltcPolicy);

  const yearSnapshots: YearSnapshot[] = [];
  const yearSnapshotIndexByPolicyYear = new Map<number, number>();

  let openState = initialState;
  const months: MonthlyEntry<TState, TDeltas>[] = [];

  for (const monthlyCareInput of monthlyCareInputs) {
    const phaseCarePlan = phaseLookup.get(monthlyCareInput.carePhase);
    if (!phaseCarePlan) continue;

    const priorMonth = months.length > 0 ? months[months.length - 1] : null;
    const month = strategy.computeMonth({
      client: mutableClient,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
    });

    if (assertIdentities) {
      assertProjectionIdentities(month);
    }

    // Snapshot the policy year on first sight.
    const { policyYear, calendarYearOffset } = month.context;
    if (!yearSnapshotIndexByPolicyYear.has(policyYear)) {
      const snapshot = strategy.buildYearSnapshot({
        client: mutableClient,
        ltcPolicy,
        policyYear,
        calendarYearOffset,
      });
      yearSnapshotIndexByPolicyYear.set(policyYear, yearSnapshots.length);
      yearSnapshots.push(snapshot);
    }

    months.push(month);
    openState = month.state;
  }

  const finalDeathBenefit = strategy.finalizeDeathBenefit({
    ltcPolicy,
    closingState: openState,
  });

  return {
    projection: { terms, yearSnapshots, months },
    finalDeathBenefit,
    closingState: openState,
  };
}
